import React, { ReactNode } from 'react';
import './Alert.css';

export interface AlertProps {
  /**
   * Alert content
   */
  children: ReactNode;

  /**
   * Alert variant
   */
  variant?: 'info' | 'success' | 'warning' | 'error';

  /**
   * Alert title
   */
  title?: string;

  /**
   * Optional icon
   */
  icon?: ReactNode;

  /**
   * Show close button
   */
  closable?: boolean;

  /**
   * Close handler
   */
  onClose?: () => void;

  /**
   * Custom class name
   */
  className?: string;
}

const defaultIcons = {
  info: 'ℹ️',
  success: '✅',
  warning: '⚠️',
  error: '❌',
};

/**
 * Alert component
 *
 * Display important messages with different severity levels
 *
 * @example
 * ```tsx
 * <Alert variant="success" title="Success!" closable onClose={() => {}}>
 *   Your changes have been saved successfully.
 * </Alert>
 * ```
 */
export function Alert({
  children,
  variant = 'info',
  title,
  icon,
  closable = false,
  onClose,
  className = '',
}: AlertProps) {
  const displayIcon = icon ?? defaultIcons[variant];

  return (
    <div className={`alert alert--${variant} ${className}`} role="alert">
      {displayIcon && <div className="alert__icon">{displayIcon}</div>}

      <div className="alert__content">
        {title && <div className="alert__title content-wrap">{title}</div>}
        <div className="alert__message content-wrap">{children}</div>
      </div>

      {closable && onClose && (
        <button
          type="button"
          className="alert__close"
          onClick={onClose}
          aria-label="Close alert"
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default Alert;
